/**
 * src/components/onboarding/WelcomeFlow.tsx — the gated, resumable customer
 * onboarding flow shown at /welcome.
 *
 * One linear path a new customer must complete before the main app unlocks:
 *
 *   intro (value screens) → address → payment (save card) → first crate → done
 *
 * Resumability: the starting step is derived from server state
 * (useOnboardingStatus), so a customer who bails after the address step
 * resumes at payment on next sign-in — on any device. The flow advances on
 * each step's success; it never traps the user (a Sign out control is always
 * present, and /profile lives above the gate).
 *
 * Compliance:
 *   - Card-on-file: the SetupIntent is usage=off_session (server-side) and we
 *     show explicit "no charge today, $X/crate/mo once stored" consent copy
 *     plus ToS/Privacy links before the card is saved.
 *   - We take the address (and, in Phase 2, validate serviceability) BEFORE
 *     asking for a card — never collect payment from someone we can't serve.
 *
 * e2e: the payment step keeps the fixture-only "[fixture] Seed test card"
 * shortcut (isTestFixtureEmail) so tests can satisfy the card requirement
 * without scripting Stripe Elements. Real customers never see it.
 */

import { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { MapPin, ArrowRight, Minus, Plus, Check, LogOut, ShieldCheck } from '../icons';
import { Elements, PaymentElement, useElements, useStripe } from '@stripe/react-stripe-js';
import { loadStripe, type Stripe } from '@stripe/stripe-js';
import { PrimaryButton, SecondaryButton } from '../ui';
import { AlphaCardModal } from '../AlphaCardModal';
import { LoadingFallback } from '../LoadingFallback';
import { useAuth } from '../../context/AuthContext';
import { useOnboardingStatus, type OnboardingStep } from '../../hooks/useOnboardingStatus';
import { markOnboardingDone } from '../OnboardingCompleteRoute';
import { AddressAutocomplete } from '../AddressAutocomplete';
import { enqueueMutation } from '../../api/mutationQueue';
import { createSetupIntent, attachPaymentMethod } from '../../api/billingApi';
import { joinWaitlist, type PlaceResolved } from '../../api/placesApi';
import { useSharedTestCard } from '../../config/billing';
import { PRICING, formatPrice } from '../../utils/pricing';
import step1Img from '../../assets/onboarding/step1.png';
import step2Img from '../../assets/onboarding/step2.png';
import step3Img from '../../assets/onboarding/step3.png';
import step4Img from '../../assets/onboarding/step4.png';

const PUBLISHABLE_KEY = import.meta.env.VITE_STRIPE_PUBLISHABLE_KEY as string | undefined;
const stripePromise: Promise<Stripe | null> = PUBLISHABLE_KEY
  ? loadStripe(PUBLISHABLE_KEY)
  : Promise.resolve(null);

type Phase = 'intro' | 'address' | 'payment' | 'order' | 'done' | 'out_of_area';

interface InfoStep { image: string; title: string; body: string }

const INFO_STEPS: InfoStep[] = [
  { image: step1Img, title: 'Order a crate', body: 'We deliver an empty storage crate right to your door. No trips to the store.' },
  { image: step2Img, title: 'Pack your stuff', body: 'Fill the crate with whatever you want stored. Snap photos so you can find things later.' },
  { image: step3Img, title: 'Schedule a pickup', body: 'When you’re ready, a driver comes to your door and takes the crate to our secure storage.' },
  { image: step4Img, title: 'Get it back anytime', body: 'Need something? Request a delivery and your crate comes right back — on your schedule.' },
];

/** Map the server-derived step to the phase the flow should open on. */
function initialPhaseFor(step: OnboardingStep): Phase | null {
  if (step === 'address') return 'intro';
  if (step === 'payment') return 'payment';
  if (step === 'order') return 'order';
  return null; // complete → caller navigates away
}

export function WelcomeFlow() {
  const navigate = useNavigate();
  const { customerEmail, signOut } = useAuth();
  const status = useOnboardingStatus();

  const [phase, setPhase] = useState<Phase | null>(null);
  const [introIndex, setIntroIndex] = useState(0);
  const [outOfAreaZip, setOutOfAreaZip] = useState('');
  const initialisedRef = useRef(false);

  // Initialise the phase once, from the first non-loading status read. After
  // that the flow is driven by local state so background refetches don't make
  // it jump around.
  useEffect(() => {
    if (initialisedRef.current || status.loading) return;
    initialisedRef.current = true;
    if (status.complete) {
      if (customerEmail) markOnboardingDone(customerEmail);
      navigate('/dashboard', { replace: true });
      return;
    }
    setPhase(initialPhaseFor(status.step));
  }, [status.loading, status.complete, status.step, customerEmail, navigate]);

  const finishToDashboard = () => {
    if (customerEmail) markOnboardingDone(customerEmail);
    navigate('/dashboard', { replace: true });
  };

  if (phase === null) return <LoadingFallback />;

  // Progress: 3 milestone dots (address · payment · crate). Intro + the
  // out-of-area dead-end count as the address milestone.
  const milestone = phase === 'payment' ? 1 : phase === 'order' || phase === 'done' ? 2 : 0;

  return (
    <div
      data-testid="welcome-flow"
      style={{
        position: 'fixed', inset: 0, zIndex: 500,
        background: 'var(--color-background)',
        display: 'flex', flexDirection: 'column', boxSizing: 'border-box',
        paddingTop: 'calc(var(--space-4) + env(safe-area-inset-top, 0px))',
        paddingBottom: 'calc(var(--space-4) + env(safe-area-inset-bottom, 0px))',
        paddingLeft: 'var(--space-5)', paddingRight: 'var(--space-5)',
      }}
    >
      {/* Header: progress + always-available sign out */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', minHeight: 40, flexShrink: 0 }}>
        <div style={{ display: 'flex', gap: 'var(--space-2)' }} aria-hidden="true">
          {[0, 1, 2].map((i) => (
            <div key={i} style={{
              width: i === milestone ? 22 : 8, height: 8, borderRadius: 'var(--radius-pill)',
              backgroundColor: i <= milestone ? 'var(--color-primary)' : 'var(--color-border)',
              transition: 'all 0.2s ease',
            }} />
          ))}
        </div>
        <button
          type="button"
          data-testid="welcome-sign-out"
          onClick={() => { void signOut(); }}
          style={{
            display: 'inline-flex', alignItems: 'center', gap: 6,
            background: 'none', border: 'none', cursor: 'pointer',
            color: 'var(--color-text-muted)', fontSize: 'var(--fs-sm)', fontWeight: 500,
            fontFamily: 'inherit', padding: 'var(--space-2)', minHeight: 40,
          }}
        >
          <LogOut size={14} aria-hidden="true" /> Sign out
        </button>
      </div>

      {phase === 'intro' && (
        <IntroPanel
          index={introIndex}
          onNext={() => {
            if (introIndex < INFO_STEPS.length - 1) setIntroIndex((i) => i + 1);
            else setPhase('address');
          }}
          onSkip={() => setPhase('address')}
        />
      )}

      {phase === 'address' && (
        <AddressPanel
          initial={(status.profile as Record<string, unknown> | null)?.['address_components'] as string | undefined}
          onSaved={() => { status.refetch(); setPhase('payment'); }}
          onOutOfArea={(zip) => { setOutOfAreaZip(zip); setPhase('out_of_area'); }}
        />
      )}

      {phase === 'out_of_area' && (
        <OutOfAreaPanel email={customerEmail} zip={outOfAreaZip} onBack={() => setPhase('address')} />
      )}

      {phase === 'payment' && (
        <PaymentPanel
          email={customerEmail}
          onSaved={() => { status.refetch(); setPhase('order'); }}
        />
      )}

      {phase === 'order' && (
        <OrderPanel
          address={status.profile?.address ?? undefined}
          onOrdered={() => { status.refetch(); setPhase('done'); }}
        />
      )}

      {phase === 'done' && <DonePanel onEnter={finishToDashboard} />}
    </div>
  );
}

/* ── Panels ──────────────────────────────────────────────────────────── */

const bodyWrap: React.CSSProperties = {
  flex: 1, minHeight: 0, overflowY: 'auto',
  display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
  gap: 'var(--space-5)', padding: 'var(--space-4) 0',
};
const ctaWrap: React.CSSProperties = { flexShrink: 0, width: '100%', maxWidth: 400, margin: '0 auto' };
const primaryBtn: React.CSSProperties = {
  width: '100%', height: 52, borderRadius: 'var(--radius-md)', border: 'none',
  backgroundColor: 'var(--color-primary)', color: 'var(--color-text-on-primary)',
  fontSize: 'var(--fs-base)', fontWeight: 600, cursor: 'pointer',
  display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 'var(--space-2)',
};
const labelStyle: React.CSSProperties = {
  display: 'block', fontSize: 'var(--fs-2xs)', fontWeight: 600, color: 'var(--color-text-muted)',
  marginBottom: 'var(--space-1)', textTransform: 'uppercase', letterSpacing: '0.04em',
};
const inputStyle: React.CSSProperties = {
  width: '100%', height: 44, padding: '0 var(--space-3)',
  border: '1px solid var(--color-border)', borderRadius: 'var(--radius-md)',
  fontSize: 'var(--fs-sm)', backgroundColor: 'var(--color-surface)',
  color: 'var(--color-text-primary)', boxSizing: 'border-box',
};
const errorBox: React.CSSProperties = {
  width: '100%', fontSize: 'var(--fs-xs)', color: 'var(--color-error)',
  padding: 'var(--space-2) var(--space-3)', backgroundColor: 'var(--color-error-surface)',
  borderRadius: 'var(--radius-md)', textAlign: 'center',
};

function IntroPanel({ index, onNext, onSkip }: { index: number; onNext: () => void; onSkip: () => void }) {
  const isFirst = index === 0;
  const step = INFO_STEPS[index]!;
  return (
    <>
      <div style={bodyWrap}>
        {/* "Welcome to Attic" shows only on the first screen, but the heading is
            always rendered (just visibility:hidden on the others) so it reserves
            the same space on every screen — that keeps all four illustrations at
            exactly the same height. */}
        <div style={{ textAlign: 'center', visibility: isFirst ? 'visible' : 'hidden' }}>
          <h2 style={{ fontFamily: 'var(--font-display)', fontSize: 'var(--fs-2xl)', fontWeight: 600, color: 'var(--color-text-primary)', margin: 0 }}>
            Welcome to Attic
          </h2>
          <p style={{ fontSize: 'var(--fs-base)', color: 'var(--color-text-muted)', margin: 'var(--space-1) 0 0' }}>
            Here&rsquo;s how it works.
          </p>
        </div>
        <img
          src={step.image}
          alt=""
          aria-hidden="true"
          style={{ width: 'min(64vw, 248px)', height: 'auto', borderRadius: 'var(--radius-lg)', display: 'block' }}
        />
        {/* Fixed min-height so a 2- vs 3-line body can't change the stack height
            and shift the illustration up — every screen's image stays level. */}
        <div style={{ textAlign: 'center', maxWidth: 340, minHeight: 124 }}>
          <h3 style={{ fontFamily: 'var(--font-display)', fontSize: 'var(--fs-xl)', fontWeight: 600, color: 'var(--color-text-primary)', margin: 0 }}>{step.title}</h3>
          <p style={{ fontSize: 'var(--fs-base)', color: 'var(--color-text-muted)', lineHeight: 1.6, marginTop: 'var(--space-3)' }}>{step.body}</p>
        </div>
      </div>
      <div style={ctaWrap}>
        <button type="button" data-testid="welcome-next" onClick={onNext} style={primaryBtn}>
          {isFirst ? 'Get started' : 'Next'} <ArrowRight size={16} aria-hidden="true" />
        </button>
        <button
          type="button" data-testid="welcome-skip" onClick={onSkip}
          style={{ width: '100%', marginTop: 'var(--space-2)', background: 'none', border: 'none', color: 'var(--color-text-muted)', cursor: 'pointer', fontSize: 'var(--fs-sm)', fontWeight: 500, fontFamily: 'inherit', minHeight: 40 }}
        >
          Skip
        </button>
      </div>
    </>
  );
}

interface AddressPanelProps {
  initial?: string | undefined;
  onSaved: () => void;
  onOutOfArea: (zip: string) => void;
}

function componentsComplete(c: PlaceResolved['components']): boolean {
  return !!(c.street && c.city && c.state && /^\d{5}$/.test(c.zip));
}

function AddressPanel({ initial, onSaved, onOutOfArea }: AddressPanelProps) {
  const seed = useMemo(() => {
    try { return initial ? (JSON.parse(initial) as Record<string, string>) : {}; } catch { return {}; }
  }, [initial]);

  const [mode, setMode] = useState<'search' | 'manual'>('search');
  const [resolved, setResolved] = useState<PlaceResolved | null>(null);
  const [apt, setApt] = useState('');
  // Manual fallback fields.
  const [street, setStreet] = useState(seed.street ?? '');
  const [city, setCity] = useState(seed.city ?? '');
  const [addrState, setAddrState] = useState(seed.state ?? '');
  const [zip, setZip] = useState(seed.zip ?? '');
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  const saveAddress = async (
    components: { street: string; apt: string; city: string; state: string; zip: string },
    coords: { lat: number; lng: number } | null,
  ) => {
    const line1 = components.apt ? `${components.street}, ${components.apt}` : components.street;
    const fullAddress = `${line1}, ${components.city}, ${components.state} ${components.zip}`;
    const fields: Record<string, unknown> = {
      address: fullAddress,
      address_components: JSON.stringify(components),
    };
    if (coords) { fields.lat = coords.lat; fields.lng = coords.lng; }
    setSaving(true);
    setError(null);
    try {
      await enqueueMutation({ type: 'update_profile', payload: { fields } });
      onSaved();
    } catch {
      setError('Could not save — try again');
      setSaving(false);
    }
  };

  const continueResolved = () => {
    if (!resolved) return;
    const c = resolved.components;
    if (!componentsComplete(c)) { setError('Pick a full street address, or enter it manually.'); return; }
    if (!resolved.serviceable) { onOutOfArea(c.zip); return; }
    void saveAddress(
      { street: c.street, apt: apt.trim(), city: c.city, state: c.state, zip: c.zip },
      resolved.lat != null && resolved.lng != null ? { lat: resolved.lat, lng: resolved.lng } : null,
    );
  };

  const continueManual = () => {
    if (!street.trim()) return setError('Street address is required');
    if (!city.trim()) return setError('City is required');
    if (!addrState.trim()) return setError('State is required');
    if (!/^\d{5}$/.test(zip.trim())) return setError('Enter a valid 5-digit ZIP');
    // Manual entries skip the serviceability gate (no precise coords — the
    // server falls back to Nominatim for routing). The autocomplete path is
    // where we gate on coverage.
    void saveAddress(
      { street: street.trim(), apt: apt.trim(), city: city.trim(), state: addrState.trim(), zip: zip.trim() },
      null,
    );
  };

  const notServiceable = !!resolved && componentsComplete(resolved.components) && !resolved.serviceable;

  return (
    <>
      <div style={{ ...bodyWrap, justifyContent: 'flex-start', paddingTop: 'var(--space-4)' }}>
        <div style={{ width: '100%', maxWidth: 400, display: 'flex', flexDirection: 'column', gap: 'var(--space-4)' }}>
          <div style={{ textAlign: 'center' }}>
            <div style={{ width: 72, height: 72, borderRadius: '50%', backgroundColor: 'var(--color-primary-light)', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto var(--space-3)' }}>
              <MapPin size={32} color="var(--color-primary)" aria-hidden="true" />
            </div>
            <h3 style={{ fontFamily: 'var(--font-display)', fontSize: 'var(--fs-xl)', fontWeight: 600, color: 'var(--color-text-primary)', margin: 0 }}>Where do we deliver?</h3>
            <p style={{ fontSize: 'var(--fs-sm)', color: 'var(--color-text-muted)', lineHeight: 1.5, marginTop: 'var(--space-2)' }}>We need your address to drop off and pick up crates.</p>
          </div>

          {error && <div role="alert" data-testid="welcome-address-error" style={errorBox}>{error}</div>}

          {mode === 'search' ? (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-3)' }}>
              <AddressAutocomplete
                testId="welcome-address-search"
                disabled={saving}
                onResolved={(p) => { setResolved(p); setApt(p.components.apt ?? ''); setError(null); }}
              />

              {resolved && componentsComplete(resolved.components) && (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-3)' }}>
                  <div data-testid="welcome-address-selected" style={{ padding: '12px 14px', background: 'var(--color-surface)', border: '1px solid var(--color-border)', borderRadius: 'var(--radius-md)', fontSize: 'var(--fs-sm)', color: 'var(--color-text-primary)' }}>
                    {resolved.formattedAddress}
                  </div>
                  <div>
                    <label style={labelStyle}>Apt / Suite / Unit</label>
                    <input type="text" data-testid="welcome-apt" value={apt} onChange={(e) => setApt(e.target.value)} placeholder="Optional" autoComplete="address-line2" disabled={saving} style={inputStyle} />
                  </div>
                </div>
              )}

              {notServiceable && (
                <div data-testid="welcome-address-out-of-area" style={{ padding: '12px 14px', background: 'var(--color-warning-surface)', border: '1px solid var(--color-warning-border)', borderRadius: 'var(--radius-md)', fontSize: 'var(--fs-xs)', color: 'var(--color-warning-border)', lineHeight: 1.5 }}>
                  We&rsquo;re not delivering to that area just yet — but we&rsquo;re expanding fast. Join the waitlist and we&rsquo;ll reach out the moment we&rsquo;re in your neighborhood.
                </div>
              )}

              <button type="button" data-testid="welcome-address-manual" onClick={() => { setMode('manual'); setResolved(null); setError(null); }} style={{ alignSelf: 'flex-start', background: 'none', border: 'none', color: 'var(--color-text-muted)', cursor: 'pointer', fontSize: 'var(--fs-xs)', fontWeight: 500, fontFamily: 'inherit', textDecoration: 'underline', textUnderlineOffset: 2, padding: 0 }}>
                Can&rsquo;t find it? Enter address manually
              </button>
            </div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-3)' }}>
              <div>
                <label style={labelStyle}>Street address</label>
                <input type="text" data-testid="welcome-street" value={street} onChange={(e) => setStreet(e.target.value)} placeholder="123 Main St" autoComplete="address-line1" disabled={saving} style={inputStyle} />
              </div>
              <div>
                <label style={labelStyle}>Apt / Suite / Unit</label>
                <input type="text" data-testid="welcome-apt" value={apt} onChange={(e) => setApt(e.target.value)} placeholder="Optional" autoComplete="address-line2" disabled={saving} style={inputStyle} />
              </div>
              <div>
                <label style={labelStyle}>City</label>
                <input type="text" data-testid="welcome-city" value={city} onChange={(e) => setCity(e.target.value)} placeholder="Denver" autoComplete="address-level2" disabled={saving} style={inputStyle} />
              </div>
              <div style={{ display: 'flex', gap: 'var(--space-3)' }}>
                <div style={{ flex: 1 }}>
                  <label style={labelStyle}>State</label>
                  <input type="text" data-testid="welcome-state" value={addrState} onChange={(e) => setAddrState(e.target.value.toUpperCase().slice(0, 2))} placeholder="CO" autoComplete="address-level1" disabled={saving} maxLength={2} style={{ ...inputStyle, textTransform: 'uppercase' }} />
                </div>
                <div style={{ flex: 1 }}>
                  <label style={labelStyle}>ZIP</label>
                  <input type="text" data-testid="welcome-zip" value={zip} onChange={(e) => setZip(e.target.value.replace(/\D/g, '').slice(0, 5))} placeholder="80202" autoComplete="postal-code" disabled={saving} maxLength={5} inputMode="numeric" style={inputStyle} />
                </div>
              </div>
              <button type="button" onClick={() => { setMode('search'); setError(null); }} style={{ alignSelf: 'flex-start', background: 'none', border: 'none', color: 'var(--color-text-muted)', cursor: 'pointer', fontSize: 'var(--fs-xs)', fontWeight: 500, fontFamily: 'inherit', textDecoration: 'underline', textUnderlineOffset: 2, padding: 0 }}>
                Search for my address instead
              </button>
            </div>
          )}
        </div>
      </div>

      <div style={ctaWrap}>
        {mode === 'search' ? (
          notServiceable ? (
            <button type="button" data-testid="welcome-address-waitlist" onClick={continueResolved} style={primaryBtn}>
              Join the waitlist
            </button>
          ) : (
            <button type="button" data-testid="welcome-address-continue" onClick={continueResolved} disabled={!resolved || saving} style={{ ...primaryBtn, cursor: !resolved || saving ? 'default' : 'pointer', opacity: !resolved || saving ? 0.5 : 1 }}>
              {saving ? 'Saving…' : 'Continue'}
            </button>
          )
        ) : (
          <button type="button" data-testid="welcome-address-save" onClick={continueManual} disabled={saving} style={{ ...primaryBtn, cursor: saving ? 'wait' : 'pointer', opacity: saving ? 0.7 : 1 }}>
            {saving ? 'Saving…' : 'Continue'}
          </button>
        )}
      </div>
    </>
  );
}

function OutOfAreaPanel({ email, zip, onBack }: { email: string | null; zip: string; onBack: () => void }) {
  const [joined, setJoined] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const join = async () => {
    if (!email) { setError('We couldn’t read your email — try again.'); return; }
    if (!/^\d{5}$/.test(zip)) { setError('That address is missing a ZIP — go back and reselect it.'); return; }
    setBusy(true);
    setError(null);
    try {
      await joinWaitlist(email, zip);
      setJoined(true);
    } catch {
      setError('Could not add you to the waitlist — try again.');
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      <div style={bodyWrap}>
        <div data-testid="welcome-out-of-area" style={{ textAlign: 'center', maxWidth: 360 }}>
          <div style={{ width: 84, height: 84, borderRadius: '50%', background: joined ? 'var(--color-success-surface)' : 'var(--color-warning-surface)', color: joined ? 'var(--color-success)' : 'var(--color-warning-border)', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto var(--space-4)' }}>
            {joined ? <Check size={40} strokeWidth={3} aria-hidden="true" /> : <MapPin size={38} aria-hidden="true" />}
          </div>
          <h2 style={{ fontFamily: 'var(--font-display)', fontSize: 'var(--fs-2xl)', fontWeight: 600, color: 'var(--color-text-primary)', margin: 0, letterSpacing: '-0.4px' }}>
            {joined ? 'You’re on the list' : 'Not in your area yet'}
          </h2>
          <p style={{ fontSize: 'var(--fs-base)', color: 'var(--color-text-muted)', lineHeight: 1.6, marginTop: 'var(--space-3)' }}>
            {joined
              ? 'Thanks! We’ll email you the moment Attic reaches your neighborhood — you’ll be first in line.'
              : 'We’re launching in Denver and expanding fast. Join the waitlist and we’ll reach out the moment we’re delivering to you.'}
          </p>
          {error && <div role="alert" style={{ ...errorBox, marginTop: 'var(--space-3)' }}>{error}</div>}
        </div>
      </div>
      <div style={ctaWrap}>
        {joined ? (
          <SecondaryButton onClick={onBack} data-testid="welcome-out-of-area-back" style={{ width: '100%', height: 52 }}>
            Use a different address
          </SecondaryButton>
        ) : (
          <>
            <PrimaryButton onClick={() => { void join(); }} loading={busy} disabled={busy} data-testid="welcome-join-waitlist" style={{ width: '100%' }}>
              {busy ? 'Joining…' : 'Join the waitlist'}
            </PrimaryButton>
            <button type="button" data-testid="welcome-out-of-area-back" onClick={onBack} style={{ width: '100%', marginTop: 'var(--space-2)', background: 'none', border: 'none', color: 'var(--color-text-muted)', cursor: 'pointer', fontSize: 'var(--fs-sm)', fontWeight: 500, fontFamily: 'inherit', minHeight: 40 }}>
              Use a different address
            </button>
          </>
        )}
      </div>
    </>
  );
}

function PaymentPanel({ email, onSaved }: { email: string | null; onSaved: () => void }) {
  // Real customers go through Stripe Elements. The one-tap shared card
  // survives only for e2e fixtures — a spec cannot reliably type into Stripe's
  // iframe, and seeding a card is not what those specs are testing.
  const useAlphaCard = useSharedTestCard(email);

  const [clientSecret, setClientSecret] = useState<string | null>(null);
  const [bootError, setBootError] = useState<string | null>(null);
  const [alphaOpen, setAlphaOpen] = useState(false);
  const cancelledRef = useRef(false);

  useEffect(() => {
    if (useAlphaCard) return; // one-tap path needs no SetupIntent
    cancelledRef.current = false;
    (async () => {
      try {
        const res = await createSetupIntent();
        if (!cancelledRef.current) setClientSecret(res.client_secret);
      } catch (err) {
        if (!cancelledRef.current) setBootError(err instanceof Error ? err.message : 'Could not start card setup');
      }
    })();
    return () => { cancelledRef.current = true; };
  }, [useAlphaCard]);

  const perCrate = formatPrice(PRICING.monthlyPerCrate);

  return (
    <>
      <div style={{ ...bodyWrap, justifyContent: 'flex-start', paddingTop: 'var(--space-4)' }}>
        <div style={{ width: '100%', maxWidth: 400, display: 'flex', flexDirection: 'column', gap: 'var(--space-4)' }}>
          <div style={{ textAlign: 'center' }}>
            <div style={{ width: 72, height: 72, borderRadius: '50%', backgroundColor: 'var(--color-primary-light)', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto var(--space-3)' }}>
              <ShieldCheck size={32} color="var(--color-primary)" aria-hidden="true" />
            </div>
            <h3 style={{ fontFamily: 'var(--font-display)', fontSize: 'var(--fs-xl)', fontWeight: 600, color: 'var(--color-text-primary)', margin: 0 }}>Add a payment method</h3>
            <p style={{ fontSize: 'var(--fs-sm)', color: 'var(--color-text-muted)', lineHeight: 1.5, marginTop: 'var(--space-2)' }}>
              No charge for adding your card. You&rsquo;re billed <strong style={{ color: 'var(--color-text-primary)' }}>{perCrate}/crate per month</strong> from the moment you order your crates.
            </p>
          </div>

          {useAlphaCard ? (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-3)' }}>
              {/* e2e fixture only — mirrors the AlphaCardModal. */}
              <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '12px 14px', borderRadius: 'var(--radius-md)', border: '1px solid var(--color-border)', background: 'var(--color-surface)' }}>
                <ShieldCheck size={18} color="var(--color-primary)" aria-hidden="true" />
                <div style={{ fontSize: 'var(--fs-sm)', color: 'var(--color-text-primary)', fontFamily: 'var(--font-mono, monospace)' }}>VISA •••• 4242</div>
                <div style={{ marginLeft: 'auto', fontSize: 'var(--fs-2xs)', color: 'var(--color-text-muted)' }}>test card</div>
              </div>
              <PrimaryButton
                data-testid="onboarding-step-seed-test-card"
                onClick={() => setAlphaOpen(true)}
                style={{ width: '100%' }}
              >
                Set up my card
              </PrimaryButton>
              <p style={{ fontSize: 'var(--fs-2xs)', color: 'var(--color-text-muted)', lineHeight: 1.5, textAlign: 'center', margin: 0 }}>
                This account uses a shared test card — no real charges.
              </p>
            </div>
          ) : (
            <>
              {!PUBLISHABLE_KEY && <div role="alert" style={errorBox}>Payments aren&rsquo;t configured for this build.</div>}
              {bootError && <div role="alert" style={errorBox}>{bootError}</div>}
              {PUBLISHABLE_KEY && !bootError && !clientSecret && (
                <div style={{ fontSize: 'var(--fs-sm)', color: 'var(--color-text-muted)', textAlign: 'center' }}>Loading secure card form…</div>
              )}

              {clientSecret && (
                <Elements
                  stripe={stripePromise}
                  options={{ clientSecret, appearance: { theme: 'stripe', variables: { colorPrimary: 'var(--attic-terracotta)', colorBackground: '#FFFAF1', colorText: 'var(--attic-ink)', fontFamily: 'inherit', borderRadius: '8px' } } }}
                >
                  <CardCapture onSaved={onSaved} />
                </Elements>
              )}

              <p style={{ fontSize: 'var(--fs-2xs)', color: 'var(--color-text-muted)', lineHeight: 1.5, textAlign: 'center', margin: 0 }}>
                By saving your card you authorize Attic to charge it for monthly storage once your crates are stored. Cancel anytime. See our{' '}
                <a href="/termsofservice" style={{ color: 'var(--color-text-muted)', textDecoration: 'underline', textUnderlineOffset: 2 }}>Terms</a> and{' '}
                <a href="/privacypolicy" style={{ color: 'var(--color-text-muted)', textDecoration: 'underline', textUnderlineOffset: 2 }}>Privacy Policy</a>.
              </p>
            </>
          )}
        </div>
      </div>

      {alphaOpen && (
        <AlphaCardModal onSuccess={() => { setAlphaOpen(false); onSaved(); }} onClose={() => setAlphaOpen(false)} />
      )}
    </>
  );
}

function CardCapture({ onSaved }: { onSaved: () => void }) {
  const stripe = useStripe();
  const elements = useElements();
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const returnUrl = useMemo(
    () => (typeof window !== 'undefined' ? `${window.location.origin}/welcome` : 'https://app.attic.it.com/welcome'),
    [],
  );

  const save = async () => {
    if (!stripe || !elements) return;
    setSubmitting(true);
    setError(null);
    const { error: confirmErr, setupIntent } = await stripe.confirmSetup({ elements, confirmParams: { return_url: returnUrl }, redirect: 'if_required' });
    if (confirmErr) { setError(confirmErr.message ?? 'Could not save card'); setSubmitting(false); return; }
    if (!setupIntent || setupIntent.status !== 'succeeded') { setError(`Card saved with status "${setupIntent?.status ?? 'unknown'}" — try again.`); setSubmitting(false); return; }
    try {
      await attachPaymentMethod(setupIntent.id);
      onSaved();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not finalise card on file');
      setSubmitting(false);
    }
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-3)' }}>
      <div style={{ background: 'var(--color-surface)', border: '1px solid var(--color-border)', borderRadius: 14, padding: 16 }}>
        <PaymentElement />
      </div>
      {error && <div role="alert" data-testid="welcome-payment-error" style={errorBox}>{error}</div>}
      <PrimaryButton onClick={() => { void save(); }} disabled={submitting || !stripe || !elements} data-testid="welcome-payment-save" style={{ width: '100%' }}>
        {submitting ? 'Saving…' : 'Save card'}
      </PrimaryButton>
    </div>
  );
}

function OrderPanel({ address, onOrdered }: { address?: string | undefined; onOrdered: () => void }) {
  const [count, setCount] = useState(1);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const monthly = formatPrice(count * PRICING.monthlyPerCrate);

  const place = async () => {
    setSubmitting(true);
    setError(null);
    try {
      const tomorrow = new Date(Date.now() + 24 * 60 * 60 * 1000).toISOString().slice(0, 10);
      await enqueueMutation({
        type: 'order_new_crates',
        payload: { requested_crate_count: count, scheduled_date: tomorrow, time_window: 'asap', pickup_address: address },
      });
      onOrdered();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not place your order — try again');
      setSubmitting(false);
    }
  };

  return (
    <>
      <div style={{ ...bodyWrap, justifyContent: 'flex-start', paddingTop: 'var(--space-4)' }}>
        <div style={{ width: '100%', maxWidth: 400, display: 'flex', flexDirection: 'column', gap: 'var(--space-4)' }}>
          <div style={{ textAlign: 'center' }}>
            <h3 style={{ fontFamily: 'var(--font-display)', fontSize: 'var(--fs-xl)', fontWeight: 600, color: 'var(--color-text-primary)', margin: 0 }}>Order your first crates</h3>
            <p style={{ fontSize: 'var(--fs-sm)', color: 'var(--color-text-muted)', lineHeight: 1.5, marginTop: 'var(--space-2)' }}>Free drop-off within a couple of days. Most folks start with 3–4.</p>
          </div>

          <div style={{ padding: '16px 18px', background: 'var(--color-surface)', borderRadius: 'var(--radius-lg)', border: '1px solid var(--color-border)', display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 'var(--space-3)' }}>
            <div style={{ fontSize: 'var(--fs-sm)', fontWeight: 600, color: 'var(--color-text-primary)' }}>How many crates?</div>
            <div role="group" aria-label="Crate count" style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '5px 8px', background: 'var(--color-background)', borderRadius: 'var(--radius-pill)', border: '1px solid var(--color-border)' }}>
              <button type="button" data-testid="welcome-order-decrement" onClick={() => setCount((c) => Math.max(1, c - 1))} disabled={submitting || count <= 1} aria-label="Fewer crates" style={{ width: 32, height: 32, borderRadius: '50%', background: 'var(--color-surface)', border: '1px solid var(--color-border)', display: 'flex', alignItems: 'center', justifyContent: 'center', color: count <= 1 ? 'var(--color-text-muted)' : 'var(--color-text-primary)', cursor: count <= 1 || submitting ? 'not-allowed' : 'pointer', opacity: count <= 1 ? 0.5 : 1, padding: 0 }}>
                <Minus size={14} strokeWidth={2.5} aria-hidden="true" />
              </button>
              <span data-testid="welcome-order-count" aria-live="polite" style={{ fontFamily: 'var(--font-display)', fontSize: 'var(--fs-2xl)', fontWeight: 600, color: 'var(--color-text-primary)', minWidth: 24, textAlign: 'center' }}>{count}</span>
              <button type="button" data-testid="welcome-order-increment" onClick={() => setCount((c) => Math.min(10, c + 1))} disabled={submitting || count >= 10} aria-label="More crates" style={{ width: 32, height: 32, borderRadius: '50%', background: 'var(--color-primary)', border: 'none', color: 'var(--attic-on-espresso)', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: count >= 10 || submitting ? 'not-allowed' : 'pointer', opacity: count >= 10 ? 0.6 : 1, padding: 0 }}>
                <Plus size={14} strokeWidth={2.5} aria-hidden="true" />
              </button>
            </div>
          </div>

          <div style={{ padding: '14px 16px', background: 'var(--color-warning-surface)', borderRadius: 'var(--radius-lg)', border: '1px solid var(--color-warning-border)' }}>
            <PricingRow label="Crate drop-off" value="Free" />
            <PricingRow label={`Monthly storage (${count} × ${formatPrice(PRICING.monthlyPerCrate)})`} value={`${monthly}/mo`} />
            <div style={{ borderTop: '1px dashed rgba(122,87,30,0.3)', margin: '8px 0 6px' }} />
            <div style={{ fontSize: 'var(--fs-2xs)', color: 'var(--color-warning-border)', lineHeight: 1.45 }}>Billed from the day you order — you&rsquo;re reserving each crate, whether it&rsquo;s at home or in storage.</div>
          </div>

          {error && <div role="alert" data-testid="welcome-order-error" style={errorBox}>{error}</div>}
        </div>
      </div>
      <div style={ctaWrap}>
        <PrimaryButton onClick={() => { void place(); }} loading={submitting} disabled={submitting} data-testid="welcome-place-order" style={{ width: '100%' }}>
          {submitting ? 'Placing order…' : `Place order · ${count} crate${count === 1 ? '' : 's'}`}
        </PrimaryButton>
      </div>
    </>
  );
}

function PricingRow({ label, value }: { label: string; value: string }) {
  return (
    <div style={{ display: 'flex', justifyContent: 'space-between', padding: '5px 0', fontSize: 'var(--fs-xs)' }}>
      <span style={{ color: 'var(--color-text-primary)', opacity: 0.8 }}>{label}</span>
      <span style={{ fontWeight: 600, color: 'var(--color-text-primary)' }}>{value}</span>
    </div>
  );
}

function DonePanel({ onEnter }: { onEnter: () => void }) {
  return (
    <>
      <div style={bodyWrap}>
        <div data-testid="welcome-done" style={{ textAlign: 'center', maxWidth: 360 }}>
          <div style={{ width: 84, height: 84, borderRadius: '50%', background: 'var(--color-success-surface)', color: 'var(--color-success)', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto var(--space-4)' }}>
            <Check size={40} strokeWidth={3} aria-hidden="true" />
          </div>
          <h2 style={{ fontFamily: 'var(--font-display)', fontSize: 'var(--fs-2xl)', fontWeight: 600, color: 'var(--color-text-primary)', margin: 0, letterSpacing: '-0.4px' }}>You&rsquo;re all set</h2>
          <p style={{ fontSize: 'var(--fs-base)', color: 'var(--color-text-muted)', lineHeight: 1.6, marginTop: 'var(--space-3)' }}>
            Your crates are on the way — we&rsquo;ll drop them off within a couple of days and let you know when a driver&rsquo;s headed over.
          </p>
        </div>
      </div>
      <div style={ctaWrap}>
        <SecondaryButton onClick={onEnter} data-testid="welcome-enter-app" style={{ width: '100%', height: 52 }}>
          Go to my dashboard
        </SecondaryButton>
      </div>
    </>
  );
}
